import type { FileExplorerFilter } from "@app/components/file_explorer/types";
import { Button } from "@dust-tt/sparkle";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const FILTER_CHIPS: {
  value: FileExplorerFilter;
  labelKey: string;
  defaultLabel: string;
}[] = [
  { value: "all", labelKey: "pods.fileExplorerFilterAll", defaultLabel: "All" },
  {
    value: "nodes",
    labelKey: "pods.fileExplorerFilterKnowledge",
    defaultLabel: "Knowledge",
  },
  {
    value: "tables",
    labelKey: "pods.fileExplorerFilterTables",
    defaultLabel: "Tables",
  },
  {
    value: "frames",
    labelKey: "pods.fileExplorerFilterFrames",
    defaultLabel: "Frames",
  },
  {
    value: "texts",
    labelKey: "pods.fileExplorerFilterTexts",
    defaultLabel: "Texts",
  },
  {
    value: "folders",
    labelKey: "pods.fileExplorerFilterFolders",
    defaultLabel: "Folders",
  },
  {
    value: "images",
    labelKey: "pods.fileExplorerFilterImages",
    defaultLabel: "Images",
  },
  {
    value: "code",
    labelKey: "pods.fileExplorerFilterCode",
    defaultLabel: "Code",
  },
];

interface FileExplorerFiltersProps {
  active: FileExplorerFilter;
  onActiveChange: (v: FileExplorerFilter) => void;
  counts: Partial<Record<FileExplorerFilter, number>>;
}

export function FileExplorerFilters({
  active,
  onActiveChange,
  counts,
}: FileExplorerFiltersProps) {
  const { t } = useTranslation();
  // "All" stays pinned first. Remaining chips are sorted by count desc, ties broken by the
  // canonical order defined in FILTER_CHIPS.
  const orderedChips = useMemo(() => {
    const [allChip, ...rest] = FILTER_CHIPS;
    const visible = rest.filter((chip) => (counts[chip.value] ?? 0) > 0);
    visible.sort((a, b) => (counts[b.value] ?? 0) - (counts[a.value] ?? 0));

    return [allChip, ...visible].map((chip) => ({
      value: chip.value,
      label: t(chip.labelKey, chip.defaultLabel),
    }));
  }, [counts, t]);

  // If only one it's "All" so there's nothing to filter against.
  if (orderedChips.length <= 1) {
    return null;
  }

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
      {orderedChips.map(({ value, label }) => {
        const count = value === "all" ? undefined : counts[value];
        return (
          <Button
            key={value}
            size="xs"
            variant={active === value ? "primary" : "outline"}
            label={label}
            isCounter={count !== undefined}
            counterValue={count !== undefined ? String(count) : undefined}
            onClick={() => onActiveChange(value)}
          />
        );
      })}
    </div>
  );
}
